import React from "react";
import { Outlet } from "react-router-dom";
import styled from "styled-components";

/**
 * `height` (not `min-height`) — a real ceiling, not just a floor. Every other route here still
 * overflows/scrolls at the page level exactly as before (nothing here clips — `overflow` stays
 * the `visible` default, so taller-than-viewport content still paints past this box and the
 * document still scrolls for it); the only thing a definite `height` changes is that `Content`
 * below now has an actual bound to flex-shrink against instead of an infinite floor, which is
 * what lets a route opt into "fill the viewport, scroll internally" instead (see
 * `CandidateAssignmentsScreen.tsx`'s own fill-chain doc comment for the one route that does).
 */
const Shell = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--dark060);
`;

const Content = styled.main`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * Customer stack layout shell.
 *
 * Deliberately header-less/nav-less: each customer org is provisioned exactly ONE interface (spec
 * decision — see the PR that removed the old multi-interface sidebar), so there is never a set of
 * sibling interfaces to link between. The old collapsible left sidebar (customer logo/name +
 * `enabledInterfaces` nav list + mobile hamburger/drawer) is gone entirely, not just hidden — every
 * concrete interface screen now owns its OWN full header/branding instead:
 *
 *   - `/ab-vault/candidate-assignments` — `ScreenShell`'s AB logo + "Candidate Assignment" title
 *     (`CandidateAssignmentsScreen.tsx`), themed via `VaultThemeProvider`'s `--ab*` teal palette.
 *   - `/spotter/*` — `SpotterHeader`'s dark sticky "Spotter" wordmark + breadcrumb + progress bar +
 *     nested nav (`features/spotter/SpotterHeader.tsx`), themed via `SpotterThemeProvider`'s `--spotter*`
 *     slate/amber palette.
 *
 * This component is therefore just the routing outlet + the shared dark background — rendering a
 * second, generic top bar here would duplicate chrome every real screen already provides. Also: no
 * customer iconik logo anywhere in the app (the dead `customerLogoUrl` field that used to be wired
 * through the `GET /api/context` contract/backend for this was removed entirely — see
 * `useCustomerContext.ts`'s `CustomerContext` type and `apps/customer-api/src/routes/context.ts`).
 */
const CustomerLayout = () => (
  <Shell>
    <Content>
      <Outlet />
    </Content>
  </Shell>
);

export default CustomerLayout;
